import type { ReactNode } from 'react'

/**
 * The frame every widget renders inside.
 *
 * Consistency is structural here rather than conventional. A widget supplies
 * content and never chrome — no padding of its own, no label styling, no font
 * size — because a style guide that widgets are trusted to follow will have
 * drifted by the fourth widget. Everything below is the only way to draw a
 * label or a number on this surface.
 */
export function Tile({
  label,
  tone,
  children,
}: {
  label: string
  /** Only track status sets this. See the note in dashboard.css. */
  tone?: 'green' | 'yellow' | 'red' | 'critical' | 'finished'
  children: ReactNode
}) {
  return (
    <div className="dash-tile" data-tone={tone}>
      <div className="dash-label">{label}</div>
      <div className="dash-body">{children}</div>
    </div>
  )
}

/**
 * The one big number, at the one datum size.
 *
 * Every single-value widget uses this, which is what makes them all match: the
 * size comes from the grid cell, so it is the same in a 1x1 tile and a 4x2 one.
 *
 * `as` picks the face rather than the size — figures that are times are set in
 * the mono face, everything else in the UI face.
 */
export function Datum({
  value,
  sub,
  as = 'number',
}: {
  value: ReactNode
  /** A qualifier that belongs beside the number, like "of 21" or "P4". */
  sub?: ReactNode
  as?: 'number' | 'time' | 'text'
}) {
  const cls =
    as === 'time' ? 'dash-datum dash-datum--time' : as === 'text' ? 'dash-datum dash-datum--text' : 'dash-datum'
  return (
    <div className={cls}>
      <span>{value}</span>
      {sub != null && <span className="dash-sub">{sub}</span>}
    </div>
  )
}

/**
 * What a widget shows when the thing it displays has not happened yet.
 *
 * An em dash rather than a zero or a spinner: before the first lap there is no
 * lap time, and "0.000" would be a claim about a lap nobody has driven.
 */
export function NoValue({ as = 'number' }: { as?: 'number' | 'time' | 'text' }) {
  return <Datum value={<span className="dash-muted">—</span>} as={as} />
}

/** A tile that is deliberately blank, with the reason on it. */
export function DeadTile({ reason }: { reason: string }) {
  return (
    <div className="dash-tile dash-tile--dead">
      <span className="dash-dead-text">{reason}</span>
    </div>
  )
}

/**
 * Two or three figures across one tile — sector times, most obviously.
 *
 * Part of the sanctioned vocabulary rather than something each widget lays out
 * for itself, so that a three-across tile has the same rhythm wherever it
 * appears. Splits are set smaller than a datum because three of them share the
 * space one datum would have.
 */
export function Splits({
  items,
}: {
  items: { label: string; value: ReactNode; flagged?: boolean; sub?: ReactNode }[]
}) {
  return (
    <div className="dash-splits">
      {items.map((item) => (
        <div className="dash-split" key={item.label} data-flagged={item.flagged || undefined}>
          <div className="dash-split-label">{item.label}</div>
          <div className="dash-split-value">{item.value}</div>
          {/* Rendered whenever any split has one, so three columns keep the
              same height and the tile does not resettle as figures arrive. */}
          {item.sub != null && <div className="dash-split-sub">{item.sub}</div>}
        </div>
      ))}
    </div>
  )
}
